import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { Icons } from '../components/icons';
import {
  actionLabel,
  resourceLabel,
  humanizeDetail,
  parseDetailRows,
  tenantLabel,
  userLabel,
} from '../lib/auditLabels';

export function AuditLogs() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ action: '', resource_type: '', search: '', from: '', to: '' });
  const pageSize = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: '' + page, page_size: '' + pageSize });
    if (filters.action) params.set('action', filters.action);
    if (filters.resource_type) params.set('resource_type', filters.resource_type);
    if (filters.search) params.set('search', filters.search);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);

    apiFetch('/api/v1/platform/audit-logs?' + params.toString())
      .then((d) => {
        if (d.code === 0) {
          setList(d.data.items || []);
          setTotal(d.data.total || 0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const actionColors = (a) => {
    if (!a) return 'tag-gray';
    if (a.startsWith('sop.')) return 'tag-green';
    if (a.startsWith('workflow.')) return 'tag-blue';
    if (a.startsWith('execution.')) return 'tag-orange';
    if (a.startsWith('document.')) return 'tag-purple';
    if (a.startsWith('station.')) return 'tag-red';
    if (a.startsWith('tenant.')) return 'tag-blue';
    return 'tag-gray';
  };

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 650, marginBottom: 16 }}>
        <Icons.Shield /> 操作审计
      </h2>

      <div className="audit-filters">
        <select
          className="audit-select"
          value={filters.action}
          onChange={(e) => {
            setFilters({ ...filters, action: e.target.value });
            setPage(1);
          }}
        >
          <option value="">全部操作</option>
          <option value="sop.create">SOP创建</option>
          <option value="sop.update">SOP更新</option>
          <option value="sop.publish">SOP发布</option>
          <option value="sop.published">SOP已发布</option>
          <option value="workflow.create">审批发起</option>
          <option value="workflow.approve">审批通过</option>
          <option value="workflow.reject">审批驳回</option>
          <option value="execution.start">开始执行</option>
          <option value="execution.started">执行已开始</option>
          <option value="execution.complete">执行完成</option>
          <option value="execution.completed">执行已完成</option>
          <option value="document.upload">文档上传</option>
          <option value="tenant.create">租户开户</option>
          <option value="tenant.suspend">租户暂停</option>
          <option value="tenant.activate">租户激活</option>
        </select>
        <select
          className="audit-select"
          value={filters.resource_type}
          onChange={(e) => {
            setFilters({ ...filters, resource_type: e.target.value });
            setPage(1);
          }}
        >
          <option value="">全部资源</option>
          <option value="sop">SOP</option>
          <option value="workflow">审批</option>
          <option value="execution">执行</option>
          <option value="document">文档</option>
          <option value="station">工站</option>
          <option value="tenant">租户</option>
          <option value="user">用户</option>
        </select>
        <input
          className="form-input"
          placeholder="搜索租户/用户/操作…"
          value={filters.search}
          onChange={(e) => {
            setFilters({ ...filters, search: e.target.value });
            setPage(1);
          }}
          style={{ maxWidth: 220 }}
        />
        <input
          className="form-input"
          type="date"
          value={filters.from}
          onChange={(e) => {
            setFilters({ ...filters, from: e.target.value });
            setPage(1);
          }}
          style={{ maxWidth: 140 }}
          title="开始日期"
        />
        <input
          className="form-input"
          type="date"
          value={filters.to}
          onChange={(e) => {
            setFilters({ ...filters, to: e.target.value });
            setPage(1);
          }}
          style={{ maxWidth: 140 }}
          title="结束日期"
        />
        <button
          className="btn btn-outline btn-xs"
          onClick={() => {
            setFilters({ action: '', resource_type: '', search: '', from: '', to: '' });
            setPage(1);
          }}
        >
          重置
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="empty">
            <span className="spinner" />
          </div>
        ) : list.length === 0 ? (
          <div className="empty">
            <Icons.Audit />
            <div className="empty-text">暂无审计记录</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="col-date">时间</th>
                  <th className="col-shrink">租户</th>
                  <th className="col-shrink">用户</th>
                  <th className="col-shrink">操作</th>
                  <th className="col-shrink">资源</th>
                  <th className="col-detail">详情</th>
                </tr>
              </thead>
              <tbody>
                {list.map((l) => {
                  const detailText = humanizeDetail(l.detail);
                  const tText = tenantLabel(l);
                  const uText = userLabel(l);
                  const detailRows = expanded === l.id ? parseDetailRows(l.detail) : [];
                  return (
                    <React.Fragment key={l.id}>
                      <tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                      >
                        <td className="col-date cell-muted">
                          {new Date(l.created_at).toLocaleString('zh-CN')}
                        </td>
                        <td title={l.tenant_id || ''}>
                          <div style={{ fontWeight: 560, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tText}
                          </div>
                          {l.tenant_code && l.tenant_name && l.tenant_name !== l.tenant_code ? (
                            <div className="cell-muted" style={{ fontSize: 11 }}>
                              {l.tenant_code}
                            </div>
                          ) : null}
                        </td>
                        <td title={l.user_id || ''}>{uText}</td>
                        <td>
                          <span className={`audit-action-tag ${actionColors(l.action)}`}>
                            {actionLabel(l.action)}
                          </span>
                        </td>
                        <td>
                          <span className="tag tag-gray" style={{ fontSize: 11 }} title={l.resource_type || ''}>
                            {resourceLabel(l.resource_type) || '—'}
                          </span>
                        </td>
                        <td className="col-detail cell-muted" title={detailText || l.detail || ''}>
                          {detailText
                            ? detailText.length > 72
                              ? detailText.substring(0, 72) + '…'
                              : detailText
                            : '—'}
                        </td>
                      </tr>
                      {expanded === l.id && (
                        <tr className="audit-row-expanded">
                          <td colSpan={6}>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 10,
                                marginBottom: 12,
                                fontSize: 12,
                              }}
                            >
                              <div>
                                <div className="cell-muted">租户</div>
                                <div>{tText}</div>
                              </div>
                              <div>
                                <div className="cell-muted">用户</div>
                                <div>{uText}</div>
                              </div>
                              <div>
                                <div className="cell-muted">操作</div>
                                <div>
                                  {actionLabel(l.action)}
                                  <span className="cell-muted" style={{ marginLeft: 6 }}>
                                    ({l.action})
                                  </span>
                                </div>
                              </div>
                              <div>
                                <div className="cell-muted">资源</div>
                                <div>
                                  {resourceLabel(l.resource_type) || '—'}
                                  {l.resource_id ? (
                                    <span className="cell-muted" style={{ marginLeft: 6 }}>
                                      {String(l.resource_id).slice(0, 8)}…
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <div>
                                <div className="cell-muted">IP</div>
                                <div>{l.ip_address || '—'}</div>
                              </div>
                              <div>
                                <div className="cell-muted">客户端</div>
                                <div title={l.user_agent || ''}>
                                  {l.user_agent
                                    ? l.user_agent.length > 48
                                      ? l.user_agent.substring(0, 48) + '…'
                                      : l.user_agent
                                    : '—'}
                                </div>
                              </div>
                            </div>
                            {detailRows.length > 0 ? (
                              <div>
                                <div className="cell-muted" style={{ marginBottom: 6, fontSize: 12 }}>
                                  详情
                                </div>
                                <table className="admin-table" style={{ fontSize: 12 }}>
                                  <tbody>
                                    {detailRows.map((row) => (
                                      <tr key={row.key + row.value}>
                                        <td style={{ width: 120, color: 'var(--brand-text-muted)' }}>
                                          {row.label}
                                        </td>
                                        <td>{row.value || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="cell-muted" style={{ fontSize: 12 }}>
                                无额外详情
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  上一页
                </button>
                <span>
                  第 {page} / {totalPages} 页 (共 {total} 条)
                </span>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  下一页
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
